import React, { cloneElement, useMemo, useRef, useState, memo, useCallback } from 'react';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import Popover, { type PopoverProps } from '@mui/material/Popover';

import { MenuItemProps } from 'components/side-menu/menu-item/menu-item.props';
import { Distance, Text } from 'common/common-components.styled';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { DefaultTheme, useTheme } from 'styled-components';
import {
  StyledIconContainer,
  StyledMenuItemContainer,
  StyledSubtitle
} from 'components/side-menu/menu-item/menu-item.styled';
import SubMenuItemComponent from 'components/side-menu/sub-menu-item';
import IconHover from 'components/icon-hover';
import { MainMenuTitles } from '../side-menu-enums';
import { StyledIndicatorForCollapsedMenu } from '../sub-menu-item/sub-menu-item.styled';
import Tooltip from '@mui/material/Tooltip';
import InfoChip from '../../info-chip';
import { MainMenuItem } from '../side-menu-interfaces';

const MenuItem = ({ expanded, item, toggleExpandItem, isMinimized }: MenuItemProps) => {
  const isDashboardTourActive = useTypedSelector(state => state.app.dashboardTourIsStarted) as boolean;
  const theme = useTheme();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const menuItemRef = useRef<HTMLDivElement | null>(null);

  const { newFiles } = useTypedSelector(state => state['app'].downloadManager);

  const hasSubMenuItems = item.subMenuItems.length > 0;
  const menuItemId = `${item.menuTitle.replace(/\s/g, '')}MenuTitle`;

  const handlePopoverOpen = useCallback(() => {
    setIsPopoverOpen(true);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const handleMenuItemMouseEnter = useCallback(() => {
    if (isMinimized && hasSubMenuItems) {
      handlePopoverOpen();
    }
  }, [isMinimized, hasSubMenuItems, handlePopoverOpen]);

  const handleMenuItemClick = useCallback(() => {
    toggleExpandItem(item);
  }, [item, toggleExpandItem]);

  const renderSubMenuItems = (showMenuTitle: boolean) =>
    item.subMenuItems.map((subMenuItem, index) => (
      <SubMenuItemComponent
        key={subMenuItem.title}
        subMenuItem={subMenuItem}
        closePopover={isMinimized ? handlePopoverClose : undefined}
        showMenuTitle={showMenuTitle}
        isFirstItem={!index}
        isLastItem={index === item.subMenuItems.length - 1}
      />
    ));

  const popoverPaperProps = useMemo(
    () => ({
      ...getMenuItemsPaperProps(theme, handlePopoverOpen, handlePopoverClose)
    }),
    [theme, handlePopoverOpen, handlePopoverClose]
  );

  const checkIndicator = (menuItem: MainMenuItem) => {
    let isIndicatorShown = false;

    if (menuItem.indicator) {
      switch (menuItem.menuTitle) {
        case MainMenuTitles.DownloadsAndFaq: {
          isIndicatorShown = menuItem?.indicator && newFiles && newFiles > 0;
          break;
        }
      }
    }

    return isIndicatorShown;
  };

  return (
    <StyledMenuItemContainer open={expanded || isPopoverOpen}>
      <StyledSubtitle
        ref={menuItemRef}
        onClick={handleMenuItemClick}
        id={menuItemId}
        open={expanded || isPopoverOpen}
        dashboardTourIsStarted={isDashboardTourActive}
        onMouseEnter={handleMenuItemMouseEnter}
        onMouseLeave={handlePopoverClose}
        isMinimized={isMinimized}
      >
        <StyledIconContainer align='center' position='relative'>
          <IconHover height='25px'>
            {cloneElement(item.mainMenuImg, {
              width: 25,
              height: 25,
              color: theme.colors.grey600
            })}
          </IconHover>

          {Boolean(checkIndicator(item)) && isMinimized && <StyledIndicatorForCollapsedMenu />}
        </StyledIconContainer>

        {!isMinimized && (
          <>
            <Distance side='20' />
            <span style={{ whiteSpace: 'nowrap' }}>{item.menuTitle}</span>
          </>
        )}

        {item.infoChip && !isMinimized && (
          <>
            <Distance side='15' />
            <Tooltip title={item.infoChipTooltip || ''}>
              <div>
                <InfoChip color={theme.colors.white} bgcolor={theme.colors.grey400} text={item.infoChip} />
              </div>
            </Tooltip>
          </>
        )}
      </StyledSubtitle>

      <Collapse in={expanded} unmountOnExit>
        {renderSubMenuItems(!isMinimized)}
      </Collapse>

      <Popover
        {...getPopoverProps(theme, handlePopoverOpen, handlePopoverClose)}
        open={isPopoverOpen && !expanded}
        anchorEl={menuItemRef.current}
        onClose={handlePopoverClose}
        PaperProps={popoverPaperProps}
        TransitionComponent={Fade}
      >
        <div>
          <Text bold color='grey600' uppercase padding='15px 23px'>
            {item.menuTitle}
          </Text>
          {renderSubMenuItems(true)}
        </div>
      </Popover>
    </StyledMenuItemContainer>
  );
};

const getPopoverProps = (
  theme: DefaultTheme,
  onMouseEnter: () => void,
  onMouseLeave: () => void
): Partial<PopoverProps> => ({
  sx: { pointerEvents: 'none' },
  anchorOrigin: { vertical: 'center', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: getMenuItemsPaperProps(theme, onMouseEnter, onMouseLeave),
  TransitionProps: { timeout: 300 }
});

export const getMenuItemsPaperProps = (theme: DefaultTheme, onMouseEnter: () => void, onMouseLeave: () => void) => ({
  elevation: 0,
  onMouseEnter,
  onMouseLeave,
  sx: {
    overflow: 'visible',
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: -3.7,
    bgcolor: theme.colors.grey50,
    pointerEvents: 'auto',
    minWidth: '270px'
  }
});

export default memo(MenuItem);
